import React, { useEffect, useState } from 'react';

import DeleteRounded from '@mui/icons-material/DeleteRounded';
import { Skeleton, Stack } from '@mui/material';
import { useNavigate } from 'react-router';

import Button from '../../../../../components/Button';
import ContentCard from '../../../../../components/ContentCard';
import Modal from '../../../../../components/Modal';
import { useDeleteCourseMutation } from '../../../../../services/courses';
import { Course } from '../../../../../services/courses/types';
import { useGetLessonTemplatesQuery } from '../../../../../services/lessons';
import { useGetStreamsQuery } from '../../../../../services/streams';
import useError from '../../../../../utils/react/hooks/useError';
import useSuccess from '../../../../../utils/react/hooks/useSuccess';

interface ActionsCardProps {
  course?: Course
}

const ActionsCard = ({ course }: ActionsCardProps) => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const { data: streamsData,
    isLoading: isStreamsLoading } = useGetStreamsQuery(
    { course: course?.id }, { skip: !course },
  );
  const { data: lessonTemplatesData,
    isLoading: isLessonTemplatesLoading } = useGetLessonTemplatesQuery(
    { course: course?.id }, { skip: !course },
  );

  const [deleteCourse, {
    isSuccess: isDeleteCourseSuccess,
    isLoading: isDeleteCourseLoading,
    isError: isDeleteCourseError,
    error: deleteCourseError,
  }] = useDeleteCourseMutation();

  useEffect(() => {
    if (isDeleteCourseSuccess) {
      // Navigate up a level after a course is deleted
      navigate(`/admin/subjects/${course?.subject}`);
    }
  }, [isDeleteCourseSuccess]);

  useSuccess(isDeleteCourseSuccess, 'Course deleted');
  useError(isDeleteCourseError, deleteCourseError);

  if (!course
    || isStreamsLoading
    || isLessonTemplatesLoading
    || !streamsData
    || !lessonTemplatesData) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Actions">
      <Stack direction="row" spacing={1}>
        <Button
          color="error"
          size="medium"
          onClick={() => setDeleteModalOpen(true)}
          disabled={streamsData.data.length > 0
             || lessonTemplatesData.data.length > 0}
          startIcon={(<DeleteRounded />)}
        >
          Delete
        </Button>
      </Stack>
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Are you sure?"
        description="This will permanently delete this course."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteCourse({ courseId: course.id }),
            color: 'error',
            loading: isDeleteCourseLoading,
          },
        ]}
      />
    </ContentCard>
  );
};

export default ActionsCard;
